import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import { Icon } from '@/components/ui/icon';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { navIndicatorTransition, prefersReducedMotion } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { useUiStore } from '@/stores/ui';

interface NavItem {
  title: string;
  href: string;
  icon: string;
  group?: string;
}

interface SidebarProps {
  className?: string;
}

function SidebarToggleButton({
  isOpen,
  onToggle,
  shouldAnimate,
}: {
  isOpen: boolean;
  onToggle: () => void;
  shouldAnimate: boolean;
}) {
  const { t } = useTranslation('common');

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <motion.button
            onClick={onToggle}
            className={cn(
              'absolute right-0 top-4 z-40 translate-x-1/2',
              'flex h-6 w-6 items-center justify-center',
              'rounded-full border border-border bg-background shadow-sm',
              'hover:bg-accent hover:border-accent',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'transition-colors',
            )}
            aria-label={isOpen ? t('sidebar.collapse') : t('sidebar.expand')}
            whileHover={shouldAnimate ? { scale: 1.1 } : undefined}
            whileTap={shouldAnimate ? { scale: 0.95 } : undefined}
          >
            <Icon
              name={isOpen ? 'chevron_left' : 'chevron_right'}
              className="h-3.5 w-3.5 text-muted-foreground"
            />
          </motion.button>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={12}>
          {isOpen ? t('sidebar.collapse') : t('sidebar.expand')}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function Sidebar({ className }: SidebarProps) {
  const { sidebarOpen, toggleSidebar } = useUiStore();
  const { t } = useTranslation('common');
  const location = useLocation();
  const shouldAnimate = !prefersReducedMotion();

  const navItems: NavItem[] = [
    {
      title: t('nav.dashboard'),
      href: '/dashboard',
      icon: 'dashboard',
      group: 'main',
    },
    {
      title: t('nav.myProjects'),
      href: '/my-projects',
      icon: 'star',
      group: 'personal',
    },
    {
      title: t('nav.myTasks'),
      href: '/tasks',
      icon: 'task_alt',
      group: 'personal',
    },
    {
      title: t('nav.projects'),
      href: '/projects',
      icon: 'view_kanban',
      group: 'data',
    },
    {
      title: t('nav.issues'),
      href: '/issues',
      icon: 'bug_report',
      group: 'data',
    },
    {
      title: t('nav.testPlans'),
      href: '/test-plans',
      icon: 'assignment_turned_in',
      group: 'data',
    },
    {
      title: t('nav.documents'),
      href: '/documents',
      icon: 'description',
      group: 'data',
    },
    {
      title: t('nav.help'),
      href: '/help',
      icon: 'help_outline',
      group: 'support',
    },
  ];

  // Group navigation items
  const mainItems = navItems.filter((item) => item.group === 'main');
  const personalItems = navItems.filter((item) => item.group === 'personal');
  const dataItems = navItems.filter((item) => item.group === 'data');
  const supportItems = navItems.filter((item) => item.group === 'support');

  // Check if a path is active (handles nested routes)
  const isPathActive = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  const renderNavItem = (item: NavItem) => {
    const isActive = isPathActive(item.href);

    const linkContent = (
      <NavLink
        to={item.href}
        className={cn(
          'relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          'hover:bg-accent hover:text-accent-foreground',
          isActive ? 'text-foreground' : 'text-muted-foreground',
          !sidebarOpen && 'justify-center px-2',
        )}
      >
        {/* Animated active indicator */}
        {isActive && shouldAnimate && (
          <motion.div
            layoutId="sidebar-active-indicator"
            className="absolute inset-0 rounded-lg bg-theme-50"
            transition={navIndicatorTransition}
            style={{ zIndex: -1 }}
          />
        )}
        {isActive && !shouldAnimate && (
          <div className="absolute inset-0 rounded-lg bg-theme-50" style={{ zIndex: -1 }} />
        )}

        <Icon
          name={item.icon}
          className={cn('h-5 w-5 shrink-0 transition-colors', isActive && 'text-primary')}
        />
        <AnimatePresence>
          {sidebarOpen && (
            <motion.span
              initial={shouldAnimate ? { opacity: 0, width: 0 } : false}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden whitespace-nowrap"
            >
              {item.title}
            </motion.span>
          )}
        </AnimatePresence>
      </NavLink>
    );

    // Show tooltip when sidebar is collapsed
    if (!sidebarOpen) {
      return (
        <TooltipProvider key={item.href} delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
            <TooltipContent side="right" sideOffset={10}>
              {item.title}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return <div key={item.href}>{linkContent}</div>;
  };

  const renderGroupLabel = (label: string) =>
    sidebarOpen ? (
      <motion.div
        initial={shouldAnimate ? { opacity: 0 } : false}
        animate={{ opacity: 1 }}
        className="mb-1 mt-4 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
      >
        {label}
      </motion.div>
    ) : (
      <div className="my-2 border-t border-border" />
    );

  return (
    <motion.aside
      className={cn(
        'fixed left-0 top-16 z-30 hidden h-[calc(100vh-4rem)] overflow-visible border-r border-border bg-background md:block',
        className,
      )}
      animate={{ width: sidebarOpen ? 224 : 64 }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      style={{ fontFamily: "'Funnel Display', sans-serif" }}
    >
      {/* Toggle button positioned at sidebar edge */}
      <SidebarToggleButton
        isOpen={sidebarOpen}
        onToggle={toggleSidebar}
        shouldAnimate={shouldAnimate}
      />

      <nav className="flex flex-col gap-1 p-2">
        {/* Main */}
        {mainItems.map(renderNavItem)}

        {/* Personal */}
        {renderGroupLabel(t('sidebar.groupPersonal'))}
        {personalItems.map(renderNavItem)}

        {/* Data */}
        {renderGroupLabel(t('sidebar.groupData'))}
        {dataItems.map(renderNavItem)}

        {/* Support */}
        {renderGroupLabel(t('sidebar.groupSupport'))}
        {supportItems.map(renderNavItem)}
      </nav>
    </motion.aside>
  );
}
